import React, { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  LaborSalesTaxRuleDTO,
  LaborSalesTaxWorkJurisdictionIdentifier,
} from '../../common/CommonInterfaces';
import { ToolTipCommonComponent } from '../../common/CommonComponents';
import {
  CLIENT_FACING_LABOR_SALES_TAX_TOOLTIPS,
  LABOR_SALES_TAX_CHARGE_TAX_LABEL,
  LABOR_SALES_TAX_DO_NOT_CHARGE_TAX_LABEL,
  LABOR_SALES_TAX_RULES_SECTION_LABEL,
  LABOR_SALES_TAX_USE_DEFAULT_LABEL,
} from '../agency-data/AgencyLaborSalesTaxClientTooltips';

type LaborSalesTaxJurisdictionViewRow = {
  readonly jurisdictionKey: LaborSalesTaxWorkJurisdictionIdentifier;
  readonly jurisdictionLabel: string;
};

const LABOR_SALES_TAX_VIEW_JURISDICTIONS: readonly LaborSalesTaxJurisdictionViewRow[] = [
  { jurisdictionKey: 'ONTARIO', jurisdictionLabel: 'Ontario (HST)' },
  { jurisdictionKey: 'BRITISH_COLUMBIA', jurisdictionLabel: 'British Columbia (GST)' },
  { jurisdictionKey: 'ALBERTA', jurisdictionLabel: 'Alberta (GST)' },
];

const formatLaborSalesTaxRuleDisplay = (
  laborSalesTaxRule: LaborSalesTaxRuleDTO
): string => {
  if (laborSalesTaxRule.laborSalesTaxApplicability === 'NO_TAX_AT_ALL') {
    return LABOR_SALES_TAX_DO_NOT_CHARGE_TAX_LABEL;
  }
  if (laborSalesTaxRule.laborSalesTaxApplicability === 'TAX_APPLICABLE_ON_FULL_AMOUNT') {
    if (laborSalesTaxRule.taxRate == null) {
      return LABOR_SALES_TAX_CHARGE_TAX_LABEL;
    }
    const ratePercent: number = Math.round(laborSalesTaxRule.taxRate * 100);
    return `${LABOR_SALES_TAX_CHARGE_TAX_LABEL} at ${ratePercent}%`;
  }
  return LABOR_SALES_TAX_CHARGE_TAX_LABEL;
};

type LaborSalesTaxRulesSummaryLayout = 'compact' | 'cardRows';

type LaborSalesTaxRulesSummaryProps = {
  readonly laborSalesTaxRules: readonly LaborSalesTaxRuleDTO[] | null | undefined;
  /** cardRows always lists Ontario, British Columbia, and Alberta on the worker type card. */
  readonly layout?: LaborSalesTaxRulesSummaryLayout;
};

const LaborSalesTaxRulesSummary = ({
  laborSalesTaxRules,
  layout = 'compact',
}: LaborSalesTaxRulesSummaryProps): React.ReactElement | null => {
  const [isSalesTaxSectionExpanded, setIsSalesTaxSectionExpanded] = useState<boolean>(false);
  const rulesByJurisdiction: ReadonlyMap<
    LaborSalesTaxWorkJurisdictionIdentifier,
    LaborSalesTaxRuleDTO
  > = new Map(
    (laborSalesTaxRules ?? []).map((laborSalesTaxRule: LaborSalesTaxRuleDTO) => [
      laborSalesTaxRule.workJurisdictionIdentifier,
      laborSalesTaxRule,
    ])
  );

  const definedJurisdictionRows: readonly LaborSalesTaxJurisdictionViewRow[] =
    LABOR_SALES_TAX_VIEW_JURISDICTIONS.filter(
      (jurisdictionViewRow: LaborSalesTaxJurisdictionViewRow): boolean =>
        rulesByJurisdiction.has(jurisdictionViewRow.jurisdictionKey)
    );

  const hasAnySalesTaxCharge: boolean = definedJurisdictionRows.some(
    (jurisdictionViewRow: LaborSalesTaxJurisdictionViewRow): boolean => {
      const laborSalesTaxRule: LaborSalesTaxRuleDTO | undefined = rulesByJurisdiction.get(
        jurisdictionViewRow.jurisdictionKey
      );
      return laborSalesTaxRule?.laborSalesTaxApplicability === 'TAX_APPLICABLE_ON_FULL_AMOUNT';
    }
  );

  if (layout === 'cardRows') {
    return (
      <div>
        <button
          type="button"
          onClick={(): void => {
            setIsSalesTaxSectionExpanded((isExpanded: boolean): boolean => !isExpanded);
          }}
          aria-expanded={isSalesTaxSectionExpanded}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            padding: '0 0 12px',
            border: 'none',
            borderBottom: '1px solid #f3f4f6',
            background: 'transparent',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <ToolTipCommonComponent
            toolTipText={CLIENT_FACING_LABOR_SALES_TAX_TOOLTIPS.rulesSection}
          >
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#2c68bd' }}>
              {LABOR_SALES_TAX_RULES_SECTION_LABEL}
            </div>
          </ToolTipCommonComponent>
          <ExpandMoreIcon
            sx={{
              fontSize: '20px',
              color: '#6b7280',
              transform: isSalesTaxSectionExpanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s ease',
            }}
          />
        </button>
        {isSalesTaxSectionExpanded
          ? LABOR_SALES_TAX_VIEW_JURISDICTIONS.map(
          (jurisdictionViewRow: LaborSalesTaxJurisdictionViewRow, jurisdictionIndex: number) => {
            const isLastRow: boolean =
              jurisdictionIndex === LABOR_SALES_TAX_VIEW_JURISDICTIONS.length - 1;
            const laborSalesTaxRule: LaborSalesTaxRuleDTO | undefined = rulesByJurisdiction.get(
              jurisdictionViewRow.jurisdictionKey
            );
            const taxConfigDisplay: string =
              laborSalesTaxRule == null
                ? LABOR_SALES_TAX_USE_DEFAULT_LABEL
                : formatLaborSalesTaxRuleDisplay(laborSalesTaxRule);
            return (
              <div
                key={jurisdictionViewRow.jurisdictionKey}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  borderBottom: isLastRow ? 'none' : '1px solid #f3f4f6',
                }}
              >
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#2c68bd' }}>
                  {jurisdictionViewRow.jurisdictionLabel}
                </div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', textAlign: 'right' }}>
                  {taxConfigDisplay}
                </div>
              </div>
            );
          }
        )
          : null}
      </div>
    );
  }

  if (definedJurisdictionRows.length === 0) {
    return null;
  }

  return (
    <div className="labor-sales-tax-rules-summary">
      <div className="labor-sales-tax-rules-summary-header">
        {hasAnySalesTaxCharge ? (
          <ToolTipCommonComponent
            toolTipText={CLIENT_FACING_LABOR_SALES_TAX_TOOLTIPS.rulesSection}
          >
            <div>{LABOR_SALES_TAX_RULES_SECTION_LABEL}</div>
          </ToolTipCommonComponent>
        ) : (
          <div aria-hidden="true">{'\u00A0'}</div>
        )}
      </div>
      {definedJurisdictionRows.map(
        (jurisdictionViewRow: LaborSalesTaxJurisdictionViewRow, jurisdictionIndex: number) => {
          const isLastRow: boolean =
            jurisdictionIndex === definedJurisdictionRows.length - 1;
          const laborSalesTaxRule: LaborSalesTaxRuleDTO | undefined = rulesByJurisdiction.get(
            jurisdictionViewRow.jurisdictionKey
          );
          if (laborSalesTaxRule == null) {
            return null;
          }
          return (
            <div
              key={jurisdictionViewRow.jurisdictionKey}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: hasAnySalesTaxCharge ? 'space-between' : 'center',
                flexDirection: hasAnySalesTaxCharge ? 'row' : 'column',
                gap: hasAnySalesTaxCharge ? '12px' : '2px',
                paddingBottom: isLastRow ? '0' : '12px',
                borderBottom: isLastRow ? 'none' : '1px solid #f3f4f6',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                {jurisdictionViewRow.jurisdictionLabel}
              </div>
              <div
                style={{
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#1f2937',
                  textAlign: hasAnySalesTaxCharge ? 'right' : 'center',
                }}
              >
                {formatLaborSalesTaxRuleDisplay(laborSalesTaxRule)}
              </div>
            </div>
          );
        }
      )}
    </div>
  );
};

export {
  LaborSalesTaxRulesSummary,
  LaborSalesTaxRulesSummaryLayout,
  LaborSalesTaxRulesSummaryProps,
};
